import Head from 'next/head';
import { StatelessSelector, Pill } from '@care/react-component-lib';
import { Button, Grid } from '@mui/material';
import Header from '@/components/Header';
import { CARE_DATES, CARE_DATE_LABELS, DAY_CARE_DATE_LABELS } from '@/constants';
import { useAppDispatch } from '@/components/AppState';
import { SxClassProps } from '@/types/common';

const classes: SxClassProps = {
  nextButtonContainer: (theme) => ({
    padding: theme.spacing(3, 0),
    marginBottom: theme.spacing(34.5),
  }),
  selector: (theme) => ({
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(4),
    },
    '& .MuiListItem-root': {
      marginBottom: theme.spacing(0),
      [theme.breakpoints.up('md')]: {
        marginBottom: theme.spacing(-0.5),
      },
      padding: theme.spacing('5px', 0),
      '& > label': {
        padding: theme.spacing(2, 2.5),
      },
    },
  }),
  mainContainer: (theme) => ({
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  }),
};

interface CareDatePageProps {
  appAction: 'setCareDate' | 'setPetCareDate' | 'setTutoringDate' | 'setHousekeepingDate';
  dateOptions?: Array<{ label: string; value: any }>;
  disabledNextButton?: boolean;
  handleNext: () => void;
  heading: string;
  selectedCareDate: any;
  selectorName: string;
}

export const dayCareOptions = [
  { label: DAY_CARE_DATE_LABELS.IMMEDIATELY, value: CARE_DATES.RIGHT_NOW },
  { label: DAY_CARE_DATE_LABELS.WITHIN_A_WEEK, value: CARE_DATES.WITHIN_A_WEEK },
  { label: DAY_CARE_DATE_LABELS.IN_1_2_MONTHS, value: CARE_DATES.IN_1_2_MONTHS },
  { label: DAY_CARE_DATE_LABELS.NO_RUSH, value: CARE_DATES.JUST_BROWSING },
];

export const options = [
  { label: CARE_DATE_LABELS.RIGHT_NOW, value: CARE_DATES.RIGHT_NOW },
  { label: CARE_DATE_LABELS.WITHIN_A_WEEK, value: CARE_DATES.WITHIN_A_WEEK },
  { label: CARE_DATE_LABELS.IN_1_2_MONTHS, value: CARE_DATES.IN_1_2_MONTHS },
  { label: CARE_DATE_LABELS.JUST_BROWSING, value: CARE_DATES.JUST_BROWSING },
];

function CareDatePage({
  appAction,
  dateOptions,
  handleNext,
  heading,
  disabledNextButton,
  selectedCareDate,
  selectorName,
}: CareDatePageProps) {
  const dispatch = useAppDispatch();
  const selectorDateOptions = dateOptions ?? options;

  const onChangeDateHandler = (value: string[]) => {
    if (!value.length) {
      return;
    }
    const selection = value[0];

    dispatch({ type: appAction, careDate: selection as CARE_DATES });
  };

  return (
    <>
      <Head>
        <title>{heading}</title>
      </Head>
      <Grid container sx={classes.mainContainer}>
        <Grid item xs={12}>
          <Header>{heading}</Header>
        </Grid>
        <Grid item xs={12}>
          <StatelessSelector
            single
            onChange={onChangeDateHandler}
            sx={classes.selector}
            name={selectorName}>
            {selectorDateOptions.map((option) => (
              <Pill
                key={option.value}
                size="lg"
                label={option.label}
                value={option.value}
                selected={selectedCareDate === option.value}
                unitId={`option-${option.value}`}
              />
            ))}
          </StatelessSelector>
        </Grid>
        <Grid item xs={12} sx={classes.nextButtonContainer}>
          <Button
            onClick={handleNext}
            disabled={disabledNextButton}
            variant="contained"
            color="primary"
            fullWidth
            size="large">
            Next
          </Button>
        </Grid>
      </Grid>
    </>
  );
}

CareDatePage.defaultProps = {
  disabledNextButton: false,
  dateOptions: options,
};

export default CareDatePage;
